import { alpha, Box, Input, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { getAppBuilderCurrentStyleValue } from 'app/app/editor/tabs/settings/style-editor/StylesEditorContainer';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import { ReactComponent as AllSidesIcon } from './icons/all-sides.svg';
import { ReactComponent as AllCornersIcon } from './icons/all-corners.svg';
import { ReactComponent as IndividualSideIcon } from './icons/individual-side.svg';
import { ReactComponent as IndividualCornerIcon } from './icons/individual-corner.svg';
import { useAppBuilderContext } from 'app/app/editor/context';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';

interface SizeFieldProps {
    formState: any;
    propertyName: string;
    onChangeStyle: (value: any) => void;
    uid: string;

    type: 'side' | 'corner';
}

export default function FieldBySide({ type, propertyName, formState, onChangeStyle = () => {}, uid }: SizeFieldProps) {
    const ctx = useAppBuilderContext();
    const node = uid == 'page' ? ctx.page.layout : getNodeByUid(ctx.page.layout, uid ?? '');
    const layoutSettings = node ? (node?.params?.layout ?? {}) : {};

    const [fillTypeValue, setFillTypeValue] = useState<string>('all');
    const [fieldValue, setFieldValue] = useState<any>({
        all: '',
        top: '',
        right: '',
        bottom: '',
        left: '',
    });
    const isFocusFieldRef = useRef<boolean>(false);

    const updateValues = () => {
        if (node) {
            const currentValue = getAppBuilderCurrentStyleValue(node?.params?.layout ?? {}, propertyName);
            const parts = currentValue.split(' ');
            if (parts.length == 4) {
                setFillTypeValue('individual');
                setFieldValue({ ...fieldValue, top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] });
            } else {
                setFillTypeValue('all');
                setFieldValue({ all: currentValue });
            }
        }
    };
    useEffect(() => {
        updateValues();
    }, []);

    useEffect(() => {
        if (!isFocusFieldRef.current) return;
        if (fillTypeValue == 'all') {
            onChangeStyle({ [propertyName]: fieldValue.all });
        } else {
            onChangeStyle({
                [propertyName]: [
                    fieldValue?.top > '' ? fieldValue.top : '0',
                    fieldValue?.right > '' ? fieldValue.right : '0',
                    fieldValue?.bottom > '' ? fieldValue.bottom : '0',
                    fieldValue?.left > '' ? fieldValue.left : '0',
                ].join(' '),
            });
        }
    }, [fillTypeValue, fieldValue]);

    useEffect(() => {
        if (!isFocusFieldRef.current) updateValues();
    }, [layoutSettings?.[propertyName]]);

    return (
        <Stack spacing={1}>
            <Stack direction={'row'} spacing={1}>
                <Box flexGrow={1}>
                    <Input
                        fullWidth
                        value={fillTypeValue == 'individual' ? 'Mixed' : fieldValue.all}
                        onChange={(e) => {
                            setFieldValue({ ...fieldValue, all: e.target.value });
                        }}
                        placeholder={'0px'}
                        disabled={fillTypeValue == 'individual'}
                        onFocus={() => {
                            isFocusFieldRef.current = true;
                        }}
                        onBlur={() => {
                            isFocusFieldRef.current = false;
                        }}
                    />
                </Box>
                <Box flexShrink={0}>
                    <ToggleButtonGroup
                        value={fillTypeValue}
                        exclusive
                        onChange={(event, value) => {
                            if (value !== null) {
                                setFillTypeValue(value);
                            }
                        }}
                        sx={{
                            svg: { width: '16px', height: '16px' },
                            '.Mui-selected': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            },
                        }}
                    >
                        <Tooltip title={'All ' + (type == 'side' ? 'sides' : 'corners')}>
                            <ToggleButton value="all">
                                {type == 'side' ? <AllSidesIcon /> : <AllCornersIcon />}
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title={'Individual ' + (type == 'side' ? 'sides' : 'corners')}>
                            <ToggleButton value="individual">
                                {type == 'side' ? <IndividualSideIcon /> : <IndividualCornerIcon />}
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </Box>
            </Stack>

            {fillTypeValue != 'all' && (
                <>
                    <Stack direction={'row'} spacing={1}>
                        <Stack direction={'row'}>
                            <StaticAddon>{type == 'side' ? 'T' : 'TL'}</StaticAddon>
                            <Box>
                                <Input
                                    fullWidth
                                    value={fieldValue.top}
                                    onChange={(e) => {
                                        setFieldValue({ ...fieldValue, top: e.target.value });
                                    }}
                                    placeholder={'0px'}
                                    onFocus={() => {
                                        isFocusFieldRef.current = true;
                                    }}
                                    onBlur={() => {
                                        isFocusFieldRef.current = false;
                                    }}
                                />
                            </Box>
                        </Stack>
                        <Stack direction={'row'}>
                            <StaticAddon>{type == 'side' ? 'R' : 'TR'}</StaticAddon>
                            <Box>
                                <Input
                                    fullWidth
                                    value={fieldValue.right}
                                    onChange={(e) => {
                                        setFieldValue({ ...fieldValue, right: e.target.value });
                                    }}
                                    placeholder={'0px'}
                                    onFocus={() => {
                                        isFocusFieldRef.current = true;
                                    }}
                                    onBlur={() => {
                                        isFocusFieldRef.current = false;
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                        <Stack direction={'row'}>
                            <StaticAddon>{type == 'side' ? 'B' : 'BR'}</StaticAddon>
                            <Box>
                                <Input
                                    fullWidth
                                    value={fieldValue.bottom}
                                    onChange={(e) => {
                                        setFieldValue({ ...fieldValue, bottom: e.target.value });
                                    }}
                                    placeholder={'0px'}
                                    onFocus={() => {
                                        isFocusFieldRef.current = true;
                                    }}
                                    onBlur={() => {
                                        isFocusFieldRef.current = false;
                                    }}
                                />
                            </Box>
                        </Stack>
                        <Stack direction={'row'}>
                            <StaticAddon>{type == 'side' ? 'L' : 'BL'}</StaticAddon>
                            <Box>
                                <Input
                                    fullWidth
                                    value={fieldValue.left}
                                    onChange={(e) => {
                                        setFieldValue({ ...fieldValue, left: e.target.value });
                                    }}
                                    placeholder={'0px'}
                                    onFocus={() => {
                                        isFocusFieldRef.current = true;
                                    }}
                                    onBlur={() => {
                                        isFocusFieldRef.current = false;
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                </>
            )}
        </Stack>
    );
}
